import { useState, useCallback, useEffect, type ComponentType } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { useTenant } from '@/contexts/TenantContext';
import { useProject } from '@/contexts/ProjectContext';
import { hasScope } from '@/lib/grants';
import SpecListPage from '@/pages/specs/SpecListPage';
import { WhatYourAiReadsSection } from '@/pages/standards/WhatYourAiReadsSection';
import { ArrowLeft, BookOpen, FileText, PenTool, ArrowRight, FolderGit2, Boxes } from 'lucide-react';
import { LoadingState, DocListSection, type DocListItem } from '@systemdox/ui';
import { PageEmptyState } from '@/components/onboarding/PageEmptyState';
import { BreadcrumbBar } from '@/components/editor/BreadcrumbBar';
import { CHECKLIST_ACTIONS } from '@/components/onboarding/SetupChecklist';
import { useConsoleUrl } from '@/hooks/useConsoleUrl';
import { loadDesignDecisions } from '@/lib/designDecisions';
import { docIdFromSearch, withoutDocParam } from '@/lib/docDeepLink';
import { cn } from '@/lib/utils';
import { docsApi } from '@/services/docsApi';
import { specsApi } from '@/services/specsApi';
import type { Repo } from '@/services/githubApi';

/** The three lifecycle pillars pinned on the Docs hub. Each opens its own
 *  section under /docs — Requirements and Design list typed spec documents,
 *  Components lists every connected repository's docs. They share one card,
 *  one section shell and one "Find item" box (specs/changes/docs-pillar-parity). */
export type DocsPillar = 'requirements' | 'components' | 'design';

/** Copy + accents for the pinned pillar cards — colours follow the Hub's
 *  lifecycle palette (Requirements blue, Components teal, Design fuchsia —
 *  teal, not emerald, which the dashboard's Connect tile owns).
 *  `docType` is present only for the spec-backed pillars; Components lists
 *  repositories, not typed documents. */
const PILLAR_META: Record<DocsPillar, {
  label: string;
  icon: ComponentType<{ className?: string }>;
  docType?: 'requirement' | 'design';
  description: string;
  countNoun: string;
  card: string;
  chip: string;
  link: string;
  pill: string;
}> = {
  requirements: {
    label: 'Requirements',
    icon: FileText,
    docType: 'requirement',
    description: 'What the system must do — requirement specs following the shared-specs convention (RFC 2119, Given/When/Then).',
    countNoun: 'specs',
    card: 'border-blue-200 dark:border-blue-900/60 bg-gradient-to-br from-blue-50 to-white dark:from-blue-950/40 dark:to-slate-900',
    chip: 'bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400',
    link: 'text-blue-600 dark:text-blue-400',
    pill: 'bg-blue-100/80 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300',
  },
  components: {
    label: 'Components',
    icon: Boxes,
    description: 'Every connected repository’s documentation — browse each codebase’s docs and publish them to your tenant docs site.',
    countNoun: 'repos',
    card: 'border-teal-200 dark:border-teal-900/60 bg-gradient-to-br from-teal-50 to-white dark:from-teal-950/40 dark:to-slate-900',
    chip: 'bg-teal-100 dark:bg-teal-900/50 text-teal-600 dark:text-teal-400',
    link: 'text-teal-600 dark:text-teal-400',
    pill: 'bg-teal-100/80 dark:bg-teal-900/40 text-teal-700 dark:text-teal-300',
  },
  design: {
    label: 'Design',
    icon: PenTool,
    docType: 'design',
    description: 'How it is built — ADRs and design documents, linked to the requirements they satisfy.',
    countNoun: 'docs',
    card: 'border-fuchsia-200 dark:border-fuchsia-900/60 bg-gradient-to-br from-fuchsia-50 to-white dark:from-fuchsia-950/40 dark:to-slate-900',
    chip: 'bg-fuchsia-100 dark:bg-fuchsia-900/50 text-fuchsia-600 dark:text-fuchsia-400',
    link: 'text-fuchsia-600 dark:text-fuchsia-400',
    pill: 'bg-fuchsia-100/80 dark:bg-fuchsia-900/40 text-fuchsia-700 dark:text-fuchsia-300',
  },
};

/**
 * The Docs hub (/docs). A single scrolling column of pillar cards
 * (Requirements, Components, Design) plus the standards the AI reads. Each
 * pillar card opens its own section under /docs, all three built from the same
 * shell — a slim "Docs" breadcrumb, a "Find item" box, then the list. Reading
 * an individual repo happens in the full-screen book reader (/book/:owner/:repo).
 *
 * The pillar sections (/docs/requirements, /docs/components, /docs/design)
 * render in place under this same component. Reading and editing a repo's docs
 * happen in the book reader that a Components row opens.
 */
export default function DocViewerPage() {
  // The hub has its own per-card affordances rather than a workspace org filter,
  // so it works on the unfiltered repo list rather than the selected org.
  const { allRepos: repos, loading } = useWorkspace();
  const { tenant } = useTenant();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // A ⌘K search result deep-links here as /docs?doc={id} (from the console it
  // cross-hosts to get here, query intact) — resolving it forwards to the book
  // reader with the document open.
  const [deepLink, setDeepLink] = useState<'resolving' | 'missing' | null>(null);

  // Requirements/Design are Build-surface content hosted inside Docs — hide
  // those two pinned cards from members whose grants don't include build:read
  // (UX only, the backend enforces). Components lists connected repositories,
  // which every member with docs access can browse, so it stays visible.
  const canBuild = hasScope(tenant?.grants, 'build:read');

  // Which section of /docs we're on (hub vs a pillar).
  const pillar: DocsPillar | null = location.pathname.endsWith('/requirements')
    ? 'requirements'
    : location.pathname.endsWith('/components')
      ? 'components'
      : location.pathname.endsWith('/design')
        ? 'design'
        : null;

  // In-surface navigations keep the query string — on localhost the
  // ?capture/?tenant params ARE the host selection, and dropping them
  // would flip the app to the console branch mid-session.
  const { search } = location;

  // Resolve the deep-linked id to its repo + path and open it in the book
  // reader, then drop the param: the navigations here carry the query string
  // on, and a spent link would re-fire on every one of them. The param is
  // consumed ONLY after the fetch settles — dropping it eagerly would flip
  // deepLinkId to null, re-run this effect, and its cleanup would cancel the
  // in-flight load, so the document would never open.
  const deepLinkId = docIdFromSearch(searchParams);
  useEffect(() => {
    if (!deepLinkId) return;
    let cancelled = false;
    setDeepLink('resolving');
    docsApi.getDoc(deepLinkId)
      .then((doc) => {
        if (cancelled) return;
        setDeepLink(null);
        // Drop the spent ?doc= before forwarding (and encode the path per
        // segment — keeps the splat slashes, escapes reserved chars): the book
        // URL, and therefore its "Back to Docs" target, must NOT carry the doc
        // id, or returning would re-resolve it and bounce back into the reader.
        const stripped = withoutDocParam(searchParams).toString();
        const encodedPath = doc.path.split('/').map(encodeURIComponent).join('/');
        navigate(
          { pathname: `/book/${doc.repo_full_name}/${encodedPath}`, search: stripped ? `?${stripped}` : '' },
          { replace: true },
        );
      })
      .catch(() => {
        if (cancelled) return;
        setDeepLink('missing');
        setSearchParams(withoutDocParam, { replace: true });
      });
    return () => { cancelled = true; };
  }, [deepLinkId, searchParams, setSearchParams, navigate]);

  const goHome = useCallback(() => {
    navigate({ pathname: '/docs', search });
  }, [navigate, search]);

  // Reading a repo happens in the full-screen book reader, which owns the
  // per-repo navigation (SUMMARY tree, prev/next, in-page search).
  const handleRepoRead = useCallback((repoFullName: string) => {
    navigate({ pathname: `/book/${repoFullName}`, search });
  }, [navigate, search]);

  const handlePillarSelect = useCallback((p: DocsPillar) => {
    navigate({ pathname: `/docs/${p}`, search });
  }, [navigate, search]);

  if (loading) {
    return <LoadingState size="lg" label="Loading repositories..." className="h-full" />;
  }

  // Pillar sections (Requirements / Components / Design) — one shell for all
  // three: a slim "Docs" breadcrumb over the section's own list, which opens
  // with a "Find item" box. Only the body differs (typed specs vs repos).
  if (pillar) {
    const meta = PILLAR_META[pillar];
    return (
      <div className="flex flex-col h-full">
        <SubPageHeader onBack={goHome} icon={meta.icon} title={meta.label} />
        <div className="flex-1 min-h-0 overflow-y-auto">
          {pillar === 'components' ? (
            <ComponentsSection
              repos={repos}
              onRepoRead={handleRepoRead}
            />
          ) : (
            <SpecListPage docType={meta.docType} />
          )}
        </div>
      </div>
    );
  }

  // ⌘K deep-link still resolving to its document, or gone from the index.
  if (deepLink) {
    return <DeepLinkState state={deepLink} onBack={goHome} />;
  }

  // The hub itself.
  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 min-h-0 overflow-y-auto">
        <DocsHome
          repos={repos}
          canBuild={canBuild}
          onPillarSelect={handlePillarSelect}
        />
      </div>
    </div>
  );
}

/** Slim header for the in-place sub-pages, with a back link to the hub —
 *  the shared BreadcrumbBar the full editors render (consolidated-full-editor R7). */
function SubPageHeader({ onBack, icon, title, children }: {
  onBack: () => void;
  icon: ComponentType<{ className?: string }>;
  title: string;
  children?: React.ReactNode;
}) {
  return (
    <BreadcrumbBar
      crumbs={[
        { label: 'Docs', onClick: onBack },
        { label: title, icon },
      ]}
      actions={children}
    />
  );
}

/** While a search result is being resolved to its document — and when it no
 *  longer exists (unindexed since the search hit). */
function DeepLinkState({ state, onBack }: { state: 'resolving' | 'missing'; onBack: () => void }) {
  return (
    <div className="flex-1 flex items-center justify-center p-4 sm:p-8 text-center h-full">
      {state === 'resolving' ? (
        <LoadingState size="lg" />
      ) : (
        <div className="max-w-sm">
          <BookOpen className="w-8 h-8 mx-auto mb-3 text-slate-300 dark:text-slate-600" />
          <p className="text-sm text-slate-500 dark:text-slate-400">
            That document is no longer in the index. It may have been moved or unindexed since the search.
          </p>
          <button onClick={onBack}
            className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline">
            <ArrowLeft className="w-4 h-4" /> Back to Docs
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * Docs hub (/docs): the three pillar cards (Requirements, Components, Design)
 * and the standards the AI reads — the whole index on one landing page. Each
 * card opens its section; the lists themselves live in the pillar sections
 * (SpecListPage / ComponentsSection). The Validate "Checks" gates moved to the
 * Insights page (#1137): they are a feedback surface, not documentation.
 */
function DocsHome({ repos, canBuild, onPillarSelect }: {
  repos: Repo[];
  canBuild: boolean;
  onPillarSelect: (pillar: DocsPillar) => void;
}) {
  const { selected: selectedProject } = useProject();
  const [counts, setCounts] = useState<Partial<Record<DocsPillar, number>>>({});

  // Same scoping as the pillar lists (docType + selected project) so the
  // card counts always match what clicking through shows. Components counts
  // repos, which are already in hand, so only the two spec pillars fetch.
  useEffect(() => {
    if (!canBuild) return;
    let cancelled = false;
    Promise.all([
      specsApi.listSpecs({ docType: 'requirement', project: selectedProject?.projectId }).catch(() => []),
      specsApi.listSpecs({ docType: 'design', project: selectedProject?.projectId }).catch(() => []),
      // The Design card counts the design repo's committed decisions too —
      // what the pillar lists (design-pillar-reads-the-decisions).
      specsApi.getDocRepos().then((r) => loadDesignDecisions(r.designRepo)).catch(() => []),
    ]).then(([requirements, designs, decisions]) => {
      if (!cancelled) setCounts({ requirements: requirements.length, design: designs.length + decisions.length });
    });
    return () => { cancelled = true; };
  }, [canBuild, selectedProject?.projectId]);

  // Requirements and Design are build-gated; Components is always available.
  // Order keeps Components directly after Requirements (specs/changes/docs-pillar-parity).
  const pillarCards = ([
    canBuild && 'requirements',
    'components',
    canBuild && 'design',
  ].filter(Boolean) as DocsPillar[]);

  const cardCount = (key: DocsPillar) => (key === 'components' ? repos.length : counts[key]);

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Docs</h1>
        <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
          Requirements, design decisions, and repository documentation — browse, edit, and publish from one place. This is what your AI reads.
        </p>
      </div>

      {/* Column count follows the card count so a docs-only member's lone
          Components card fills the row instead of sitting in an empty grid. */}
      <div className={cn(
        'grid gap-4 mb-10',
        pillarCards.length >= 3 ? 'sm:grid-cols-2 lg:grid-cols-3' : pillarCards.length === 2 ? 'sm:grid-cols-2' : '',
      )}>
        {pillarCards.map((key) => (
          <PillarCard key={key} pillar={key} count={cardCount(key)} onClick={() => onPillarSelect(key)} />
        ))}
      </div>

      {/* What your AI reads — the workspace standards the MCP server serves
          (Tech Stack, Checks, Model Settings, Rules, Prompt Templates). Each
          card links to its editor under /docs; hidden without build access,
          like the pillar sections. */}
      {canBuild && (
        <div className="mb-10">
          <WhatYourAiReadsSection />
        </div>
      )}
    </div>
  );
}

/**
 * The Components pillar section (/docs/components) — every connected
 * repository's docs, listed with the shared DocListSection (sdx#1120), which
 * owns the "Find item" box and the org filter. Reading a repo opens the
 * full-screen book reader, where its docs are read and edited.
 */
function ComponentsSection({ repos, onRepoRead }: {
  repos: Repo[];
  onRepoRead: (repoFullName: string) => void;
}) {
  const consoleUrl = useConsoleUrl();
  // Which connected repos have at least one indexed doc, so the section can call
  // out the ones with none. `null` until the index loads (or if it fails) so we
  // never flash every repo as undocumented; space-scoped like the rest of the
  // index, so docs the caller can't read don't count towards presence. Read as
  // per-repository counts (fast-document-browsing): listing the first 1,000
  // whole documents took seconds and missed every repo sorted after them.
  const [reposWithDocs, setReposWithDocs] = useState<Set<string> | null>(null);
  useEffect(() => {
    let cancelled = false;
    docsApi.getDocCoverage()
      .then((coverage) => {
        if (!cancelled) {
          setReposWithDocs(new Set(coverage.filter((c) => c.doc_count > 0).map((c) => c.repo_full_name)));
        }
      })
      .catch((e) => { console.warn('Failed to load doc index for the undocumented-repos section:', e); });
    return () => { cancelled = true; };
  }, []);

  const items: DocListItem[] = repos.map((repo) => ({
    id: repo.full_name,
    icon: FolderGit2,
    title: repo.repo,
    location: repo.full_name,
    owner: repo.owner,
    onOpen: () => onRepoRead(repo.full_name),
    openTitle: `Open ${repo.repo} docs`,
  }));

  // Connected repos with no indexed docs — a generic empty-coverage affordance
  // shown under the list (docs-pillar-parity; sdx#1120 permits it not tracking
  // the in-list search filter).
  const reposWithoutDocs = reposWithDocs
    ? repos.filter((r) => !reposWithDocs.has(r.full_name))
    : [];
  const footer = reposWithoutDocs.length > 0 ? (
    <div className="mt-8 pt-6 border-t border-slate-200 dark:border-slate-800">
      <div className="flex items-baseline justify-between mb-1">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
          Repositories without documentation
        </h3>
        <span className="text-xs text-slate-400 dark:text-slate-500">
          {reposWithoutDocs.length} {reposWithoutDocs.length === 1 ? 'repository' : 'repositories'}
        </span>
      </div>
      <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">
        Connected repositories with no docs indexed yet — nothing here for your AI to read. Open one to configure and publish its docs.
      </p>
      <div className="flex flex-wrap gap-2">
        {reposWithoutDocs.map((repo) => (
          <button
            key={repo.full_name}
            onClick={() => onRepoRead(repo.full_name)}
            title={`Open ${repo.full_name}`}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/60 hover:border-slate-300 dark:hover:border-slate-700 hover:bg-white dark:hover:bg-slate-800 transition-colors"
          >
            <FolderGit2 className="w-3.5 h-3.5 text-slate-400 shrink-0" />
            <span className="text-xs font-medium text-slate-700 dark:text-slate-200">{repo.repo}</span>
            <span className="text-xs text-slate-400 dark:text-slate-500">{repo.owner}</span>
          </button>
        ))}
      </div>
    </div>
  ) : null;

  return (
    <DocListSection
      title="Components"
      subtitle={PILLAR_META.components.description}
      items={items}
      // GitHub connection settings live on the Build console, so the CTA is an
      // absolute cross-host URL (label shared with the checklist), omitted when
      // no tenant slug is resolvable.
      emptyState={
        <PageEmptyState
          icon={BookOpen}
          title="Your codebase, documented"
          description="Connect your GitHub account to browse each repository's markdown docs."
          customAction={consoleUrl ? { label: CHECKLIST_ACTIONS.connectGithub.label, to: consoleUrl(CHECKLIST_ACTIONS.connectGithub.to) } : undefined}
        />
      }
      noMatchLabel="No repositories match your search."
      footer={footer}
      orgLabel="Filter by GitHub organization"
    />
  );
}

/** Highlighted entry card for a pinned pillar section. */
function PillarCard({ pillar, count, onClick }: {
  pillar: DocsPillar;
  count?: number;
  onClick: () => void;
}) {
  const meta = PILLAR_META[pillar];
  const Icon = meta.icon;
  return (
    <button
      onClick={onClick}
      className={cn('group text-left p-5 rounded-2xl border transition-shadow hover:shadow-md', meta.card)}
    >
      <div className="flex items-start justify-between gap-3">
        <div className={cn('w-10 h-10 rounded-xl flex items-center justify-center', meta.chip)}>
          <Icon className="w-5 h-5" />
        </div>
        {count !== undefined && (
          <span className={cn('px-2 py-0.5 rounded-full text-xs font-semibold', meta.pill)}>
            {count} {meta.countNoun}
          </span>
        )}
      </div>
      <h3 className="mt-4 text-base font-semibold text-slate-900 dark:text-slate-100">{meta.label}</h3>
      <p className="mt-1 text-sm text-slate-500 dark:text-slate-400 leading-relaxed">{meta.description}</p>
      <span className={cn('mt-3 inline-flex items-center gap-1 text-sm font-medium', meta.link)}>
        Browse {meta.label.toLowerCase()}
        <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
      </span>
    </button>
  );
}

